import { useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Hand } from "lucide-react";
import { Dir } from "@/game/types";

const WEDGES: { d: Dir; Icon: typeof ChevronUp }[] = [
  { d: "up", Icon: ChevronUp },
  { d: "right", Icon: ChevronRight },
  { d: "down", Icon: ChevronDown },
  { d: "left", Icon: ChevronLeft },
];

export const DPad = ({ onHold }: { onHold: (d: Dir | null) => void }) => {
  const pad = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<Dir | null>(null);
  const last = useRef<Dir | null>(null);
  const pressed = useRef(false);

  const emit = (d: Dir | null) => { if (d !== last.current) { last.current = d; setActive(d); onHold(d); } };
  const dirAt = (e: React.PointerEvent): Dir | null => {
    const r = pad.current!.getBoundingClientRect();
    const dx = e.clientX - r.left - r.width / 2, dy = e.clientY - r.top - r.height / 2;
    if (Math.hypot(dx, dy) < r.width * 0.11) return null;
    return Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
  };
  const down = (e: React.PointerEvent) => { e.preventDefault(); pressed.current = true; pad.current!.setPointerCapture(e.pointerId); emit(dirAt(e)); };
  const move = (e: React.PointerEvent) => { if (pressed.current) emit(dirAt(e)); };
  const up = () => { pressed.current = false; emit(null); };

  return (
    <div ref={pad} data-testid="virtual-dpad" className="dpad touch-none select-none" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onLostPointerCapture={up} onContextMenu={(e) => e.preventDefault()}>
      {WEDGES.map(({ d, Icon }) => (
        <div key={d} data-testid={`dpad-${d}-button`} aria-label={d} className={`dpad-wedge dpad-wedge--${d} ${active === d ? "is-active" : ""}`}>
          <Icon className="dpad-wedge-icon" size={30} strokeWidth={3} />
        </div>
      ))}
      <span className="dpad-hub" aria-hidden />
    </div>
  );
};

export const PushButton = ({ onPush }: { onPush: () => void }) => (
  <button
    data-testid="dpad-push-button"
    aria-label="push"
    className="dpad-btn dpad-push touch-none select-none"
    onPointerDown={(e) => { e.preventDefault(); onPush(); }}
    onContextMenu={(e) => e.preventDefault()}
  >
    <Hand size={38} strokeWidth={2.5} />
  </button>
);
